import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
    Alert, Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
    IconButton, MenuItem, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs,
    TextField, Tooltip, Typography,
} from '@mui/material';
import {
    getBetupMatchIdByVirtualSyncId, getVirtualLeagueMatches, getVirtualLeagueSeasons, getVirtualProviderLeague,
    getVirtualProviderTeam, getVirtualSeasonTable, regenerateVirtualLeagueImage, searchVirtualProviderTeams,
    updateVirtualLeague,
} from '../../Api';
import {
    VirtualProviderLeagueDto, VirtualProviderMatchSummaryDto, VirtualProviderSeasonDto,
    VirtualProviderTeamDto, VirtualProviderTournamentStandingDto,
} from '../../models/dtos/VirtualProviderCatalogDto';
import { formatKickoff, MatchTeams } from './VirtualMatchesTable';
import VirtualTeamSearchSelect from './VirtualTeamSearchSelect';
import VirtualSeasonDetailsDialog from './VirtualSeasonDetailsDialog';

type LeagueTabKey = 'general' | 'teams' | 'seasons' | 'tournament' | 'matches';

function seasonStatusColor(status: VirtualProviderSeasonDto['status']): 'default' | 'success' | 'info' | 'warning' {
    switch (status) {
        case 'ACTIVE': return 'success';
        case 'SCHEDULED': return 'info';
        case 'CANCELLED': return 'warning';
        default: return 'default';
    }
}

function matchStateColor(state: VirtualProviderMatchSummaryDto['state']): 'default' | 'success' | 'info' | 'warning' {
    switch (state) {
        case 'Live': return 'success';
        case 'Finished': return 'info';
        case 'Cancelled': return 'warning';
        default: return 'default';
    }
}

export default function VirtualLeagueDetailsDialog(props: {
    leagueSyncId: string | null;
    onClose: () => void;
    onUpdated: (league: VirtualProviderLeagueDto) => void;
}) {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = React.useState<LeagueTabKey>('general');
    const [league, setLeague] = React.useState<VirtualProviderLeagueDto | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [regenerating, setRegenerating] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');
    const [notice, setNotice] = React.useState('');

    const [teams, setTeams] = React.useState<VirtualProviderTeamDto[]>([]);
    const [teamsLoading, setTeamsLoading] = React.useState(false);
    const [teamToAdd, setTeamToAdd] = React.useState<VirtualProviderTeamDto | null>(null);
    const [teamsBusy, setTeamsBusy] = React.useState(false);
    const [fillDialogOpen, setFillDialogOpen] = React.useState(false);
    const [fillMaxTeams, setFillMaxTeams] = React.useState('10');
    const [fillBusy, setFillBusy] = React.useState(false);
    const [fillError, setFillError] = React.useState('');

    const [seasons, setSeasons] = React.useState<VirtualProviderSeasonDto[]>([]);
    const [seasonsLoading, setSeasonsLoading] = React.useState(false);
    const [seasonsLoadedForLeagueId, setSeasonsLoadedForLeagueId] = React.useState<string | null>(null);
    const [openSeasonId, setOpenSeasonId] = React.useState<string | null>(null);

    const [tournamentLoading, setTournamentLoading] = React.useState(false);
    const [tournamentLoadedForLeagueId, setTournamentLoadedForLeagueId] = React.useState<string | null>(null);
    const [currentSeason, setCurrentSeason] = React.useState<VirtualProviderSeasonDto | null>(null);
    const [standings, setStandings] = React.useState<VirtualProviderTournamentStandingDto[]>([]);

    const [upcomingMatches, setUpcomingMatches] = React.useState<VirtualProviderMatchSummaryDto[]>([]);
    const [recentMatches, setRecentMatches] = React.useState<VirtualProviderMatchSummaryDto[]>([]);
    const [matchesLoading, setMatchesLoading] = React.useState(false);
    const [matchesLoadedForLeagueId, setMatchesLoadedForLeagueId] = React.useState<string | null>(null);
    const [resolvingMatchId, setResolvingMatchId] = React.useState<string | null>(null);

    const isFriendly = league?.leagueType === 'FRIENDLY';
    const tabDefs: LeagueTabKey[] = isFriendly
        ? ['general', 'teams', 'matches']
        : ['general', 'teams', 'seasons', 'tournament', 'matches'];
    const tabIndex = Math.max(0, tabDefs.indexOf(activeTab));

    React.useEffect(() => {
        if (!props.leagueSyncId) return;
        let cancelled = false;
        setLeague(null);
        setError('');
        setNotice('');
        setActiveTab('general');
        setTeams([]);
        setSeasons([]);
        setSeasonsLoadedForLeagueId(null);
        setCurrentSeason(null);
        setStandings([]);
        setTournamentLoadedForLeagueId(null);
        setUpcomingMatches([]);
        setRecentMatches([]);
        setMatchesLoadedForLeagueId(null);
        setLoading(true);
        getVirtualProviderLeague(props.leagueSyncId)
            .then(value => { if (!cancelled) setLeague(value); })
            .catch(reason => { if (!cancelled) setError(reason instanceof Error ? reason.message : 'Could not load league details'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [props.leagueSyncId]);

    // Fall back to General when switching into FRIENDLY hides the tab the user was on.
    React.useEffect(() => {
        if (!tabDefs.includes(activeTab)) setActiveTab('general');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFriendly]);

    React.useEffect(() => {
        if (activeTab !== 'teams' || !league) return;
        let cancelled = false;
        setTeamsLoading(true);
        Promise.all(league.teamIds.map(id => getVirtualProviderTeam(id)))
            .then(loadedTeams => { if (!cancelled) setTeams(loadedTeams); })
            .catch(reason => { if (!cancelled) setError(reason instanceof Error ? reason.message : 'Could not load league teams'); })
            .finally(() => { if (!cancelled) setTeamsLoading(false); });
        return () => { cancelled = true; };
    }, [activeTab, league]);

    React.useEffect(() => {
        if (activeTab !== 'seasons' || !league || seasonsLoadedForLeagueId === league.id) return;
        let cancelled = false;
        setSeasonsLoading(true);
        getVirtualLeagueSeasons(league.id)
            .then(value => { if (!cancelled) { setSeasons(value); setSeasonsLoadedForLeagueId(league.id); } })
            .catch(reason => { if (!cancelled) setError(reason instanceof Error ? reason.message : 'Could not load seasons'); })
            .finally(() => { if (!cancelled) setSeasonsLoading(false); });
        return () => { cancelled = true; };
    }, [activeTab, league, seasonsLoadedForLeagueId]);

    React.useEffect(() => {
        if (activeTab !== 'tournament' || !league || tournamentLoadedForLeagueId === league.id) return;
        let cancelled = false;
        setTournamentLoading(true);
        getVirtualLeagueSeasons(league.id)
            .then(async allSeasons => {
                const active = allSeasons.find(season => season.status === 'ACTIVE') ?? null;
                if (cancelled) return;
                setCurrentSeason(active);
                if (active) {
                    const table = await getVirtualSeasonTable(active.id);
                    if (!cancelled) setStandings(table);
                } else {
                    setStandings([]);
                }
                setTournamentLoadedForLeagueId(league.id);
            })
            .catch(reason => { if (!cancelled) setError(reason instanceof Error ? reason.message : 'Could not load the current tournament table'); })
            .finally(() => { if (!cancelled) setTournamentLoading(false); });
        return () => { cancelled = true; };
    }, [activeTab, league, tournamentLoadedForLeagueId]);

    React.useEffect(() => {
        if (activeTab !== 'matches' || !league || matchesLoadedForLeagueId === league.id) return;
        let cancelled = false;
        setMatchesLoading(true);
        getVirtualLeagueMatches(league.id)
            .then(matches => {
                if (cancelled) return;
                const upcoming = matches
                    .filter(match => match.state === 'Future')
                    .sort((a, b) => a.startDate.localeCompare(b.startDate))
                    .slice(0, 5);
                const recent = matches
                    .filter(match => match.state === 'Live' || match.state === 'Finished')
                    .sort((a, b) => b.startDate.localeCompare(a.startDate))
                    .slice(0, 5);
                setUpcomingMatches(upcoming);
                setRecentMatches(recent);
                setMatchesLoadedForLeagueId(league.id);
            })
            .catch(reason => { if (!cancelled) setError(reason instanceof Error ? reason.message : 'Could not load league matches'); })
            .finally(() => { if (!cancelled) setMatchesLoading(false); });
        return () => { cancelled = true; };
    }, [activeTab, league, matchesLoadedForLeagueId]);

    const regenerate = async () => {
        if (!league) return;
        setRegenerating(true);
        setError('');
        try {
            const updated = await regenerateVirtualLeagueImage(league.id);
            setLeague(updated);
            props.onUpdated(updated);
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Could not regenerate league logo');
        } finally {
            setRegenerating(false);
        }
    };

    const saveScheduleConfiguration = async () => {
        if (!league) return;
        setSaving(true);
        setError('');
        try {
            const updated = await updateVirtualLeague(league.id, {
                name: league.name,
                nameRu: translatedRussianName(league),
                description: league.description,
                descriptionRu: league.descriptionRu,
                allowedCountries: league.allowedCountries ?? null,
                teamIds: league.teamIds,
                leagueType: league.leagueType,
                speed: league.speed,
                seasonIntervalHours: league.seasonIntervalHours,
            });
            setLeague(updated);
            props.onUpdated(updated);
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Could not update league schedule settings');
        } finally {
            setSaving(false);
        }
    };

    /** Returns null on success, or the error message on failure. */
    const updateTeamIds = async (nextTeamIds: string[]): Promise<string | null> => {
        if (!league) return 'League is not loaded yet.';
        setTeamsBusy(true);
        setError('');
        try {
            const updated = await updateVirtualLeague(league.id, {
                name: league.name,
                nameRu: translatedRussianName(league),
                description: league.description,
                descriptionRu: league.descriptionRu,
                allowedCountries: league.allowedCountries ?? null,
                teamIds: nextTeamIds,
                leagueType: league.leagueType,
                speed: league.speed,
                seasonIntervalHours: league.seasonIntervalHours,
            });
            setLeague(updated);
            props.onUpdated(updated);
            return null;
        } catch (reason) {
            const message = reason instanceof Error ? reason.message : 'Could not update league teams';
            setError(message);
            return message;
        } finally {
            setTeamsBusy(false);
        }
    };

    const addTeam = async () => {
        if (!teamToAdd || !league) return;
        await updateTeamIds([...league.teamIds, teamToAdd.id]);
        setTeamToAdd(null);
    };

    const removeTeam = async (teamId: string) => {
        if (!league) return;
        await updateTeamIds(league.teamIds.filter(id => id !== teamId));
    };

    const openFillDialog = () => {
        setFillError('');
        setFillMaxTeams('10');
        setFillDialogOpen(true);
    };

    const fillWithEligibleTeams = async () => {
        if (!league) return;
        const maxTeams = Number(fillMaxTeams);
        if (!Number.isFinite(maxTeams) || maxTeams <= 0 || !Number.isInteger(maxTeams)) {
            setFillError('Enter a whole number greater than 0.');
            return;
        }
        setFillBusy(true);
        setFillError('');
        try {
            const eligible = await fetchEligibleTeams(league.sportId, league.teamIds, league.allowedCountries);
            if (eligible.length === 0) {
                setFillError('No eligible teams are available to add.');
                return;
            }
            const selected = eligible.length <= maxTeams ? eligible : pickRandom(eligible, maxTeams);
            const updateError = await updateTeamIds([...league.teamIds, ...selected.map(team => team.id)]);
            if (updateError) {
                setFillError(updateError);
                return;
            }
            setFillDialogOpen(false);
            setNotice(`Added ${selected.length} team${selected.length === 1 ? '' : 's'} to the league.`);
        } catch (reason) {
            setFillError(reason instanceof Error ? reason.message : 'Could not fill the league with eligible teams');
        } finally {
            setFillBusy(false);
        }
    };

    const openMatch = async (match: VirtualProviderMatchSummaryDto) => {
        setResolvingMatchId(match.id);
        setNotice('');
        try {
            const betupMatchId = await getBetupMatchIdByVirtualSyncId(match.id);
            if (betupMatchId == null) {
                setNotice('This match has not been synchronized to the main catalog yet.');
                return;
            }
            navigate(`/matches/${betupMatchId}`);
        } finally {
            setResolvingMatchId(null);
        }
    };

    const busy = regenerating || saving;

    return <>
        <Dialog open={props.leagueSyncId != null} onClose={busy ? undefined : props.onClose} fullWidth maxWidth="lg">
            <DialogTitle>Virtual league details</DialogTitle>
            {league ? (
                <Box sx={{ px: 3, pb: 2 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                        <Avatar src={league.imageUrl ?? undefined} variant="rounded" sx={{ width: 96, height: 96 }}>{league.name.slice(0, 1)}</Avatar>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h5">{league.name}</Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                                <Chip label={`Sport ${league.sportId}`} />
                                <Chip variant="outlined" color={league.active ? 'success' : 'default'} label={league.active ? 'Active' : 'Inactive'} />
                                <Chip variant="outlined" label={leagueTypeLabel(league.leagueType)} />
                            </Stack>
                        </Box>
                    </Stack>
                </Box>
            ) : null}
            <Tabs value={tabIndex} onChange={(_, value) => setActiveTab(tabDefs[value] ?? 'general')} sx={{ px: 3 }} variant="scrollable" scrollButtons="auto">
                <Tab label="General" />
                <Tab label={`Teams${league ? ` (${league.teamIds.length})` : ''}`} />
                {!isFriendly ? <Tab label="Seasons" /> : null}
                {!isFriendly ? <Tab label="Current tournament" /> : null}
                <Tab label="Matches" />
            </Tabs>
            <DialogContent dividers>
                {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert> : null}
                {notice ? <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert> : null}
                {loading ? <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box> : !league ? null : (
                    <>
                        {activeTab === 'general' ? (
                            <Stack spacing={3}>
                                <Box><Typography variant="subtitle2" color="text.secondary">Description (English)</Typography><Typography sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{league.description}</Typography></Box>
                                <Box><Typography variant="subtitle2" color="text.secondary">Description (Russian)</Typography><Typography sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{league.descriptionRu}</Typography></Box>
                                <Divider />
                                <Box>
                                    <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Season scheduling</Typography>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                        <TextField
                                            select
                                            fullWidth
                                            label="Competition type"
                                            value={league.leagueType}
                                            disabled={busy}
                                            onChange={event => setLeague(current => current ? { ...current, leagueType: event.target.value as VirtualProviderLeagueDto['leagueType'] } : current)}
                                        >
                                            <MenuItem value="DOMESTIC">Domestic · fixed round-robin</MenuItem>
                                            <MenuItem value="PLAYOFF">Playoff · dynamic bracket</MenuItem>
                                            <MenuItem value="FRIENDLY">Friendly · seasonless training matches</MenuItem>
                                        </TextField>
                                        <TextField
                                            select
                                            fullWidth
                                            label="Fixture speed"
                                            value={league.speed}
                                            disabled={busy}
                                            onChange={event => setLeague(current => current ? { ...current, speed: event.target.value as VirtualProviderLeagueDto['speed'] } : current)}
                                        >
                                            <MenuItem value="IMMEDIATE">Immediate · 15 minutes</MenuItem>
                                            <MenuItem value="QUICK">Quick · 1 hour</MenuItem>
                                            <MenuItem value="MEDIUM">Medium · 6 hours</MenuItem>
                                            <MenuItem value="SLOW">Slow · 12 hours</MenuItem>
                                            <MenuItem value="REALISTIC">Realistic · 1 day</MenuItem>
                                        </TextField>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Season interval (hours)"
                                            value={league.seasonIntervalHours}
                                            disabled={busy}
                                            onChange={event => {
                                                const parsed = Number(event.target.value);
                                                setLeague(current => current
                                                    ? { ...current, seasonIntervalHours: Number.isFinite(parsed) ? Math.max(0, parsed) : 0 }
                                                    : current);
                                            }}
                                            inputProps={{ min: 0 }}
                                        />
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                        Competitive changes apply to the next season. The season interval is the minimum gap kept
                                        between a completed season's end and the next season's registration window. Friendly leagues
                                        have no seasons or standings and periodically schedule training matches at the selected speed.
                                    </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                    Created {new Date(league.createdAt).toLocaleString()} · Updated {new Date(league.updatedAt).toLocaleString()} · ID {league.id}
                                </Typography>
                            </Stack>
                        ) : null}

                        {activeTab === 'teams' ? (
                            <Stack spacing={2}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                                    <VirtualTeamSearchSelect
                                        value={teamToAdd}
                                        onChange={setTeamToAdd}
                                        disabled={teamsBusy}
                                        sportId={league.sportId}
                                        excludedTeamIds={league.teamIds}
                                        allowedCountries={league.allowedCountries}
                                        label="Add a team"
                                    />
                                    <Button variant="outlined" sx={{ whiteSpace: 'nowrap' }} onClick={addTeam} disabled={!teamToAdd || teamsBusy}>
                                        Add team
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        sx={{ whiteSpace: 'nowrap' }}
                                        onClick={openFillDialog}
                                        disabled={teamsBusy || league.allowedCountries?.length === 0}
                                    >
                                        Fill with eligible teams
                                    </Button>
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                    {league.allowedCountries == null
                                        ? 'Teams from any country can be added.'
                                        : league.allowedCountries.length === 0
                                            ? 'This league allows no countries, so no team can be added.'
                                            : `Only teams from: ${league.allowedCountries.join(', ')}.`}
                                </Typography>
                                {teamsLoading ? <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box> : (
                                    teams.length === 0 ? <Typography color="text.secondary">This league has no member teams yet.</Typography> : (
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Team</TableCell>
                                                        <TableCell>Country</TableCell>
                                                        <TableCell>Status</TableCell>
                                                        <TableCell align="right">Remove</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {teams.map(team => (
                                                        <TableRow key={team.id} hover>
                                                            <TableCell>
                                                                <Stack direction="row" spacing={1.5} alignItems="center">
                                                                    <Avatar src={team.imageUrl ?? undefined} sx={{ width: 32, height: 32 }}>{team.name.slice(0, 1)}</Avatar>
                                                                    <Typography variant="body2">{team.name}</Typography>
                                                                </Stack>
                                                            </TableCell>
                                                            <TableCell>{team.originCountry ?? '—'}</TableCell>
                                                            <TableCell><Chip size="small" variant="outlined" color={team.active ? 'success' : 'default'} label={team.active ? 'Active' : 'Inactive'} /></TableCell>
                                                            <TableCell align="right">
                                                                <Tooltip title="Remove from league">
                                                                    <span>
                                                                        <IconButton color="error" disabled={teamsBusy} onClick={() => removeTeam(team.id)}>
                                                                            <DeleteOutlineIcon />
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )
                                )}
                            </Stack>
                        ) : null}

                        {activeTab === 'seasons' ? (
                            seasonsLoading ? <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box> : (
                                seasons.length === 0 ? (
                                    <Typography color="text.secondary">
                                        No seasons yet. Domestic and playoff leagues open a registration window automatically once the league is active.
                                    </Typography>
                                ) : (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Season</TableCell>
                                                    <TableCell>Status</TableCell>
                                                    <TableCell>Starts</TableCell>
                                                    <TableCell>Ended</TableCell>
                                                    <TableCell align="right">Teams</TableCell>
                                                    <TableCell align="right">Round</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {seasons.map(season => (
                                                    <TableRow key={season.id} hover sx={{ cursor: 'pointer' }} onClick={() => setOpenSeasonId(season.id)}>
                                                        <TableCell>
                                                            <Typography variant="body2">Season {season.seasonNumber}</Typography>
                                                            <Typography variant="caption" color="text.secondary">{season.leagueType} · {season.speed}</Typography>
                                                        </TableCell>
                                                        <TableCell><Chip size="small" color={seasonStatusColor(season.status)} label={season.status} /></TableCell>
                                                        <TableCell>{new Date(season.startsAt).toLocaleString()}</TableCell>
                                                        <TableCell>{season.endedAt ? new Date(season.endedAt).toLocaleString() : '—'}</TableCell>
                                                        <TableCell align="right">{season.teamIds.length}</TableCell>
                                                        <TableCell align="right">{season.currentRound}/{season.totalRounds}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )
                            )
                        ) : null}

                        {activeTab === 'tournament' ? (
                            tournamentLoading ? <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box> : (
                                !currentSeason ? (
                                    <Typography color="text.secondary">No active season right now.</Typography>
                                ) : (
                                    <Stack spacing={2}>
                                        <Stack direction="row" spacing={1} flexWrap="wrap">
                                            <Chip color="success" label={`Season ${currentSeason.seasonNumber}`} />
                                            <Chip variant="outlined" label={`Round ${currentSeason.currentRound}/${currentSeason.totalRounds}`} />
                                        </Stack>
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>#</TableCell>
                                                        <TableCell>Team</TableCell>
                                                        <TableCell align="right">P</TableCell>
                                                        <TableCell align="right">W</TableCell>
                                                        <TableCell align="right">D</TableCell>
                                                        <TableCell align="right">L</TableCell>
                                                        <TableCell align="right">SF</TableCell>
                                                        <TableCell align="right">SA</TableCell>
                                                        <TableCell align="right">Diff</TableCell>
                                                        <TableCell align="right">Pts</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {standings.map(standing => (
                                                        <TableRow key={standing.team.id}>
                                                            <TableCell>{standing.rank}</TableCell>
                                                            <TableCell>{standing.team.name}</TableCell>
                                                            <TableCell align="right">{standing.played}</TableCell>
                                                            <TableCell align="right">{standing.won}</TableCell>
                                                            <TableCell align="right">{standing.drawn}</TableCell>
                                                            <TableCell align="right">{standing.lost}</TableCell>
                                                            <TableCell align="right">{standing.scoreFor}</TableCell>
                                                            <TableCell align="right">{standing.scoreAgainst}</TableCell>
                                                            <TableCell align="right">{standing.scoreDifference}</TableCell>
                                                            <TableCell align="right"><strong>{standing.points}</strong></TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Stack>
                                )
                            )
                        ) : null}

                        {activeTab === 'matches' ? (
                            matchesLoading ? <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box> : (
                                <Stack spacing={3}>
                                    <MatchesSection
                                        title="Next scheduled matches"
                                        emptyLabel="No upcoming matches scheduled."
                                        matches={upcomingMatches}
                                        resolvingMatchId={resolvingMatchId}
                                        onOpen={openMatch}
                                    />
                                    <Divider />
                                    <MatchesSection
                                        title="Recent matches"
                                        emptyLabel="No live or finished matches yet."
                                        matches={recentMatches}
                                        resolvingMatchId={resolvingMatchId}
                                        onOpen={openMatch}
                                    />
                                </Stack>
                            )
                        ) : null}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose} disabled={busy}>Close</Button>
                {activeTab === 'general' ? <Button variant="outlined" onClick={saveScheduleConfiguration} disabled={!league || loading || busy}>
                    {saving ? <CircularProgress size={20} /> : 'Save schedule settings'}
                </Button> : null}
                {activeTab === 'general' ? <Button variant="contained" onClick={regenerate} disabled={!league || loading || busy}>
                    {regenerating ? <CircularProgress size={20} /> : league?.imageUrl ? 'Regenerate logo' : 'Generate logo'}
                </Button> : null}
            </DialogActions>
        </Dialog>
        <VirtualSeasonDetailsDialog seasonId={openSeasonId} onClose={() => setOpenSeasonId(null)} />
        <Dialog open={fillDialogOpen} onClose={fillBusy ? undefined : () => setFillDialogOpen(false)} fullWidth maxWidth="xs">
            <DialogTitle>Fill with eligible teams</DialogTitle>
            <DialogContent dividers>
                {fillError ? <Alert severity="error" sx={{ mb: 2 }}>{fillError}</Alert> : null}
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                    Adds active, same-sport teams that satisfy this league's country restrictions and are not
                    already members. If more are eligible than the maximum, teams are picked at random.
                </Typography>
                <TextField
                    autoFocus
                    fullWidth
                    type="number"
                    label="Maximum teams to add"
                    value={fillMaxTeams}
                    disabled={fillBusy}
                    onChange={event => setFillMaxTeams(event.target.value)}
                    inputProps={{ min: 1 }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setFillDialogOpen(false)} disabled={fillBusy}>Cancel</Button>
                <Button variant="contained" onClick={fillWithEligibleTeams} disabled={fillBusy}>
                    {fillBusy ? <CircularProgress size={20} /> : 'Fill'}
                </Button>
            </DialogActions>
        </Dialog>
    </>;
}

function MatchesSection(props: {
    title: string;
    emptyLabel: string;
    matches: VirtualProviderMatchSummaryDto[];
    resolvingMatchId: string | null;
    onOpen: (match: VirtualProviderMatchSummaryDto) => void;
}) {
    return (
        <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>{props.title}</Typography>
            {props.matches.length === 0 ? (
                <Typography color="text.secondary">{props.emptyLabel}</Typography>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Match</TableCell>
                                <TableCell>Kickoff</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Score</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {props.matches.map(match => (
                                <TableRow key={match.id} hover sx={{ cursor: 'pointer' }} onClick={() => props.onOpen(match)}>
                                    <TableCell><MatchTeams match={match} size={20} /></TableCell>
                                    <TableCell>{formatKickoff(match.startDate)}</TableCell>
                                    <TableCell>
                                        {props.resolvingMatchId === match.id ? <CircularProgress size={16} /> : (
                                            <Chip size="small" color={matchStateColor(match.state)} label={match.state} />
                                        )}
                                    </TableCell>
                                    <TableCell align="right">{match.state === 'Future' ? '—' : `${match.scoreHome} : ${match.scoreAway}`}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}

function translatedRussianName(league: VirtualProviderLeagueDto): string {
    if (!league.nameTranslationsJson) return league.name;
    try {
        return (JSON.parse(league.nameTranslationsJson) as { ru?: string }).ru || league.name;
    } catch {
        return league.name;
    }
}

function leagueTypeLabel(leagueType: VirtualProviderLeagueDto['leagueType']): string {
    if (leagueType === 'DOMESTIC') return 'Domestic';
    if (leagueType === 'PLAYOFF') return 'Playoff';
    return 'Friendly';
}

const TEAM_SEARCH_PAGE_SIZE = 100;

/** Fetches every active team for the sport, paginating the search endpoint, then applies the league's membership and country constraints. */
async function fetchEligibleTeams(
    sportId: number,
    excludedTeamIds: string[],
    allowedCountries: string[] | null | undefined,
): Promise<VirtualProviderTeamDto[]> {
    const excluded = new Set(excludedTeamIds);
    const collected: VirtualProviderTeamDto[] = [];
    let offset = 0;
    for (;;) {
        const page = await searchVirtualProviderTeams({ sportId, active: true, offset, limit: TEAM_SEARCH_PAGE_SIZE });
        collected.push(...page.items);
        offset += TEAM_SEARCH_PAGE_SIZE;
        if (page.items.length === 0 || offset >= page.totalItems) break;
    }
    return collected.filter(team => !excluded.has(team.id)
        && (allowedCountries == null || (team.originCountry != null && allowedCountries.includes(team.originCountry))));
}

/** Fisher-Yates partial shuffle, returning up to `count` randomly selected items. */
function pickRandom<T>(items: T[], count: number): T[] {
    const shuffled = [...items];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled.slice(0, count);
}
